import {
  useContext,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "preact/hooks";
import { PlpContext } from "@/providers/plp-provider";
import classNames from "classnames";
import { Fragment } from "preact";
import "../styles/facets.css";

export default function Facet({ facet, clearForm }) {
  const {
    setUrl,
    facets,
    customFacetDefinitions,
    urlManager,
    url,
    facetStrings,
  } = useContext(PlpContext);

  const star = `<svg class="w-[14px] h-[14px] mt-[-2px] mr-[2px]" xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none"><path fill="#D6001C" d="m7.193.353 1.614 4.97h5.226L9.806 8.394l1.614 4.97-4.227-3.071-4.228 3.071 1.615-4.97L.352 5.323h5.226L7.193.353Z"/></svg>`;
  const startEmpty = `<svg class="w-[14px] h-[14px] mt-[-2px] mr-[2px]" xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none"><path fill="#D2D2D2" d="m7.55.353 1.615 4.97h5.226l-4.228 3.071 1.615 4.97-4.228-3.071-4.228 3.071 1.615-4.97L.71 5.323h5.226L7.55.353Z"/></svg>`;

  const FROM0_5 =
    startEmpty + startEmpty + startEmpty + startEmpty + startEmpty;
  const FROM1_5 = star + startEmpty + startEmpty + startEmpty + startEmpty;
  const FROM2_5 = star + star + startEmpty + startEmpty + startEmpty;
  const FROM3_5 = star + star + star + startEmpty + startEmpty;
  const FROM4_5 = star + star + star + star + startEmpty;

  const FILTER_NAME = {
    PRICE: "Price",
  };

  const FACET_NAMESPACE = {
    OBJECT: "object",
    METAFIELD_SALSIFY: "metafieldSalsify",
  };

  const SEARCH_PARAM = {
    RANGE: "range",
    PRICE_MIN: "priceMin",
    PRICE_MAX: "priceMax",
    REFINER_FILTERS: "refinerFilters",
    START: "start",
  };

  const OPTIONS_COUNT_IN_GROUP = 5; // Coorelated to height in collection__filter-checkbox-list

  const [rangeOptions, setRangeOptions] = useState([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const getReview = (text) => {
    let data = FROM0_5;
    if (text === "review_from_1_5") {
      data = FROM1_5;
    }
    if (text === "review_from_2_5") {
      data = FROM2_5;
    }
    if (text === "review_from_3_5") {
      data = FROM3_5;
    }
    if (text === "review_from_4_5") {
      data = FROM4_5;
    }

    return (
      data +
      `<span class="ml-1 -translate-y-[2px] lg:-translate-y-[1px]"> & Up</span>`
    );
  };

  const refinerFilters = useMemo(
    () =>
      urlManager.searchParams.get(SEARCH_PARAM.REFINER_FILTERS)?.split("||"),
    [url]
  );

  const refinerFiltersSet = useMemo(
    () =>
      new Set(
        urlManager.searchParams
          .get(SEARCH_PARAM.REFINER_FILTERS)
          ?.split("||")
          .map((item) => item.split(":")[1])
          .filter(Boolean)
      ),
    [url]
  );

  const rangeFiltersSet = useMemo(() => {
    const set = new Set(
      urlManager.searchParams
        .get(SEARCH_PARAM.RANGE)
        ?.split("||")
        .map((item) => item.split(":")[1])
        .filter(Boolean)
    );
    // Price is a special case
    if (
      urlManager.searchParams.get(SEARCH_PARAM.PRICE_MIN) ||
      urlManager.searchParams.get(SEARCH_PARAM.PRICE_MAX)
    )
      set.add(FILTER_NAME.PRICE);
    return set;
  }, [url]);

  const priceMin = useMemo(
    () => +urlManager.searchParams.get(SEARCH_PARAM.PRICE_MIN),
    [url]
  );

  const priceMax = useMemo(
    () => +urlManager.searchParams.get(SEARCH_PARAM.PRICE_MAX),
    [url]
  );

  const range = useMemo(
    () => urlManager.searchParams.get(SEARCH_PARAM.RANGE)?.split("||"),
    [url]
  );

  const isChecked = useMemo(
    () => (filterName, optionName) => {
      if (filterName === "onSale") {
        return refinerFilters?.find((filter) =>
          filter.includes(`${FACET_NAMESPACE.OBJECT}:onSale:true`)
        );
      }

      if (isCustomFacet(optionName)) {
        return refinerFilters?.find((filter) =>
          filter.includes(`${optionName}:true`)
        );
      }

      return refinerFilters?.find((filter) => {
        const facetFilter = filter.replace(
          `${FACET_NAMESPACE.METAFIELD_SALSIFY}:`,
          ""
        );
        return facetFilter === `${filterName}:${optionName}`;
      });
    },
    [url]
  );

  const isCheckedReview = useMemo(
    () => (filterName, optionName) => {
      return range?.find((filter) => {
        const number = optionName.replace("review_from_", "").replace("_5", "");
        const facetOptionFilter = `metafieldBazaarVoice:Rating:[${number} TO 5]`;
        return filter === facetOptionFilter;
      });
    },
    [url]
  );

  const isCheckedRange = useMemo(
    () => (filterName, rangeOption) => {
      // Price is a special case
      if (filterName === FILTER_NAME.PRICE) {
        return (
          parseInt(rangeOption.min) === priceMin &&
          parseInt(rangeOption.max) === priceMax
        );
      }

      return range?.find((filter) => {
        const facetOptionFilter = `${FACET_NAMESPACE.METAFIELD_SALSIFY}:${filterName}:[${rangeOption.min} TO ${rangeOption.max}]`;
        return filter === facetOptionFilter;
      });
    },
    [url]
  );

  const isDisabledRange = useMemo(
    () => (rangeOption) => {
      return rangeOption.count === 0;
    },
    [url]
  );

  const isCustomFacet = useMemo(
    () => (optionName) => {
      return Object.values(customFacetDefinitions).flat().includes(optionName);
    },
    [url]
  );

  const displayName = useMemo(
    () => (facet) => {
      const friendlyName = facet.filter.overwrite?.trim();
      const salsifyName = facet.filter.name;
      return friendlyName || salsifyName;
    },
    [url]
  );

  const salsifyFacet = useMemo(
    () =>
      facets[1]?.metafieldSalsify?.find(
        (item) => item.facet === facet.filter.name
      ),
    [url, facets]
  );

  const itemName = useMemo(
    () => (item) => {
      const friendlyName = item.overwrite?.trim();
      const salsifyName = item.name;
      const salsifyOption = salsifyFacet?.list?.find(
        (option) => option.name === salsifyName
      );
      const count = salsifyOption ? salsifyOption.count : 0;
      const showCount = facet.filter.counts;
      return `${friendlyName || salsifyName} ${showCount ? `(${count})` : ""}`;
    },
    [url, facets]
  );

  const [selected, setSelected] = useState(null);

  const onChangeHandler = useCallback(
    (event) => {
      const { type, name, value } = event.target;
      setSelected({
        type,
        name,
        value,
      });

      console.log({ type, name, value });
      console.log({ facet });
      updateUrl(type, name, value);

      // urlManager.searchParams.set(SEARCH_PARAM.START, "0");

      // const facetOptionFilter = `${FACET_NAMESPACE.METAFIELD_SALSIFY}:${name}:${value}`;

      // if (!refinerFilters) {
      //   urlManager.searchParams.set(
      //     SEARCH_PARAM.REFINER_FILTERS,
      //     facetOptionFilter
      //   );

      //   return setUrl(urlManager.href);
      // }

      // const facetOptionFilterIndex = refinerFilters.indexOf(facetOptionFilter);
      // const newRefinerFilters = [...refinerFilters];
      // if (type === "radio") {
      //   newRefinerFilters.forEach((item, index) => {
      //     if (item.split(":")[1] === name)
      //       newRefinerFilters[index] = facetOptionFilter;
      //   });
      // }

      // if (facetOptionFilterIndex === -1) {
      //   newRefinerFilters.push(facetOptionFilter);
      // } else {
      //   newRefinerFilters.splice(facetOptionFilterIndex, 1);
      // }

      // urlManager.searchParams.set(
      //   SEARCH_PARAM.REFINER_FILTERS,
      //   newRefinerFilters.join("||")
      // );

      // return setUrl(urlManager.href);
    },
    [url, facets]
  );

  const updateUrl = useCallback((type, name, value) => {
    urlManager.searchParams.set(SEARCH_PARAM.START, "0");

    const facetOptionFilter = `${FACET_NAMESPACE.METAFIELD_SALSIFY}:${name}:${value}`;

    if (!refinerFilters) {
      urlManager.searchParams.set(
        SEARCH_PARAM.REFINER_FILTERS,
        facetOptionFilter
      );

      return setUrl(urlManager.href);
    }

    const facetOptionFilterIndex = refinerFilters.indexOf(facetOptionFilter);
    const newRefinerFilters = [...refinerFilters];
    if (type === "radio") {
      newRefinerFilters.forEach((item, index) => {
        if (item.split(":")[1] === name)
          newRefinerFilters[index] = facetOptionFilter;
      });
    }

    if (facetOptionFilterIndex === -1) {
      newRefinerFilters.push(facetOptionFilter);
    } else {
      newRefinerFilters.splice(facetOptionFilterIndex, 1);
    }

    urlManager.searchParams.set(
      SEARCH_PARAM.REFINER_FILTERS,
      newRefinerFilters.join("||")
    );

    return setUrl(urlManager.href);
  });

  const onReviewChangeHandler = useCallback(
    (event) => {
      const { name, value } = event.target;
      urlManager.searchParams.set(SEARCH_PARAM.START, "0");

      if (name !== "Rating") return;
      const number = value.replace("review_from_", "").replace("_5", "");
      const facetOptionFilter = `metafieldBazaarVoice:Rating:[${number} TO 5]`;

      if (!range) {
        urlManager.searchParams.set(SEARCH_PARAM.RANGE, facetOptionFilter);

        return setUrl(urlManager.href);
      }

      const rangeFilterIndex = range.indexOf(facetOptionFilter);
      const newRangeFilters = [];

      if (rangeFilterIndex === -1) {
        newRangeFilters.push(facetOptionFilter);
      } else {
        newRangeFilters.splice(rangeFilterIndex, 1);
      }

      urlManager.searchParams.set(
        SEARCH_PARAM.RANGE,
        newRangeFilters.join("||")
      );

      return setUrl(urlManager.href);
    },
    [url, facets]
  );

  const formMinValueChanged = () => {
    updateSubmitState();
  };

  const formMaxValueChanged = () => {
    updateSubmitState();
  };

  const updateSubmitState = () => {
    const disabled =
      formMinValue.current.value.length === 0 ||
      formMaxValue.current.value.length === 0;

    setSubmitDisabled(disabled);
  };

  const onSubmit = useCallback(
    (event) => {
      event.preventDefault();

      const formData = new FormData(event.target);

      const min = formData.get("Min");
      const max = formData.get("Max");
      const name = formData.get("Facet");

      if (min === "" || max === "" || name === "") {
        return;
      }

      urlManager.searchParams.set(SEARCH_PARAM.START, "0");

      if (name === FILTER_NAME.PRICE) {
        urlManager.searchParams.set(SEARCH_PARAM.PRICE_MIN, min);
        urlManager.searchParams.set(SEARCH_PARAM.PRICE_MAX, max);
        return setUrl(urlManager.href);
      }

      const facetOptionFilter = `${FACET_NAMESPACE.METAFIELD_SALSIFY}:${name}:[${min} TO ${max}]`;

      if (range === undefined) {
        urlManager.searchParams.set(SEARCH_PARAM.RANGE, facetOptionFilter);
        return setUrl(urlManager.href);
      }

      const newRangeFilters = range.filter(
        (item) => item.split(":")[1] !== name
      );

      newRangeFilters.push(facetOptionFilter);
      newRangeFilters.filter((item) => item && item.length > 0);

      urlManager.searchParams.set(
        SEARCH_PARAM.RANGE,
        newRangeFilters.join("||")
      );

      return setUrl(urlManager.href);
    },
    [url, facets]
  );

  const handleClearClick = useCallback(
    (event, facet) => {
      event.preventDefault();

      const facetOption = `${FACET_NAMESPACE.METAFIELD_SALSIFY}:${facet.filter.name}`;
      const optionRE = new RegExp(facetOption);

      // O(n) filter
      const newRefinerFilters = [
        ...refinerFilters.filter((item) => !optionRE.test(item)),
      ];

      urlManager.searchParams.set(
        SEARCH_PARAM.REFINER_FILTERS,
        newRefinerFilters.join("||")
      );

      return setUrl(urlManager.href);
    },
    [url, facets]
  );

  const clearFormCustomValues = () => {
    if (formMinValue && formMinValue.current) {
      formMinValue.current.value = "";
    }

    if (formMaxValue && formMaxValue.current) {
      formMaxValue.current.value = "";
    }
  };

  const handleClearRange = useCallback(
    (event, facet) => {
      event.preventDefault();

      clearFormCustomValues();

      // Price is a special case
      if (facet.filter.name === FILTER_NAME.PRICE) {
        urlManager.searchParams.delete(SEARCH_PARAM.PRICE_MIN);
        urlManager.searchParams.delete(SEARCH_PARAM.PRICE_MAX);

        return setUrl(urlManager.href);
      }

      const facetOption =
        facet.filter.name === "Rating"
          ? "metafieldBazaarVoice:Rating"
          : `${FACET_NAMESPACE.METAFIELD_SALSIFY}:${facet.filter.name}`;
      const optionRE = new RegExp(facetOption);

      // O(n) filter
      const newRangeFilters = [...range.filter((item) => !optionRE.test(item))];

      urlManager.searchParams.set(
        SEARCH_PARAM.RANGE,
        newRangeFilters.join("||")
      );

      return setUrl(urlManager.href);
    },
    [url, facets]
  );

  const onRangeChangeHandler = useCallback(
    (event) => {
      const { name, value, checked } = event.target;
      const { min, max } = JSON.parse(value);
      urlManager.searchParams.set(SEARCH_PARAM.START, "0");

      const facet = facets.find((facet) =>
        Object.values(facet)[0].find(
          ({ facet }) => facet === name || facet === name.toLowerCase()
        )
      );
      const facetNamespace = Object.keys(facet)[0];
      const facetOptionFilter = `${facetNamespace}:${name}:[${min} TO ${max}]`;
      if (name === FILTER_NAME.PRICE) {
        if (checked) {
          urlManager.searchParams.set(SEARCH_PARAM.PRICE_MIN, min);
          urlManager.searchParams.set(SEARCH_PARAM.PRICE_MAX, max);
        } else {
          urlManager.searchParams.delete(SEARCH_PARAM.PRICE_MIN);
          urlManager.searchParams.delete(SEARCH_PARAM.PRICE_MAX);
        }

        return setUrl(urlManager.href);
      }

      if (!range) {
        urlManager.searchParams.set(SEARCH_PARAM.RANGE, facetOptionFilter);

        return setUrl(urlManager.href);
      }

      const rangeFilterIndex = range.indexOf(facetOptionFilter);

      const newRangeFilters = [...range];

      newRangeFilters.forEach((item, index) => {
        if (item.split(":")[1] === name)
          newRangeFilters[index] = facetOptionFilter;
      });

      if (rangeFilterIndex === -1) {
        newRangeFilters.push(facetOptionFilter);
      } else {
        newRangeFilters.splice(rangeFilterIndex, 1);
      }

      urlManager.searchParams.set(
        SEARCH_PARAM.RANGE,
        newRangeFilters.join("||")
      );

      return setUrl(urlManager.href);
    },
    [url, facets]
  );

  const minus = useRef(null);
  const content = useRef(null);
  const facetBlock = useRef(null);
  const formMinValue = useRef(null);
  const formMaxValue = useRef(null);

  useEffect(() => {
    filterOptions("");
    setTimeout(resize, 100);
  }, []);

  const resize = () => {
    content.current.style.height = facet.filter.expand
      ? content.current.scrollHeight + "px"
      : "0";
  };

  useEffect(() => {
    clearFormCustomValues();
  }, [clearForm]);

  useEffect(() => {
    let options = [];

    if (!facet.filter.params) {
      options = defaultRangeOptions;
    } else {
      const fragments = facet.filter.params.split("||");
      const values = fragments[0].split(";");
      const labels = fragments[1]?.split(";");

      if (values) {
        for (let i = 0; i < values.length; i++) {
          const _values = values[i].split(":");
          const min = _values[0];
          const max = _values[1];

          const count = countProduct(min, max);

          options.push({
            min,
            max,
            label: labels[i],
            count,
          });
        }
      }
    }

    if (options) {
      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        const found = rangeOptions.find((item) => item.label === option.label);

        if (found) {
          if (found.originalCount === undefined) {
            option.originalCount = found.count;
          } else {
            option.originalCount = found.originalCount;
          }
        }
      }
    }

    setRangeOptions(options);
  }, [facets]);

  const toggle = () => {
    if (facetBlock.current.classList.contains("close")) {
      facetBlock.current.classList.remove("close");
    } else {
      facetBlock.current.classList.add("close");
    }

    if (minus.current.classList.contains("hide")) {
      minus.current.classList.remove("hide");
      content.current.style.height = content.current.scrollHeight + "px";
    } else {
      minus.current.classList.add("hide");
      content.current.style.height = "0";
    }
  };

  const isRangeFacet = useMemo(
    () => (facet) => {
      return (
        facet.filter.style === "Custom" &&
        (facet.filter.custom === "Range" ||
          facet.filter.custom === "Range/Stars")
      );
    },
    [facet]
  );

  const isRatingFacet = useMemo(
    () => (facet) => {
      return (
        facet.filter.style === "Custom" && facet.filter.custom === "Range/Stars"
      );
    },
    [facet]
  );

  const ratingData = useMemo(
    () => (facet) => {
      if (isRatingFacet) {
        const params = facet.filter.params.split("||");

        return params[1].split(";").map((item) => {
          const star = item.split("").filter((text) => text === "*");
          return `review_from_${star.length}_5`;
        });
      }
    },
    [facet]
  );

  const countProduct = (min, max) => {
    let count = 0;
    let data;

    if (facet.filter.name === FILTER_NAME.PRICE) {
      data = facets[0][FACET_NAMESPACE.OBJECT].filter(
        (item) => item.facet === facet.filter.name.toLowerCase()
      );
    } else {
      data = facets[1][FACET_NAMESPACE.METAFIELD_SALSIFY].filter(
        (item) => item.facet === facet.filter.name
      );
    }

    if (data && data.length) {
      const list = data[0].list.map((item) => {
        return {
          name: item.name,
          value: parseFloat(item.name),
          count: parseInt(item.count),
        };
      });

      const found = list.filter(
        (item) => item.value > min && item.value <= max
      );

      if (found) {
        for (let i = 0; i < found.length; i++) {
          const item = found[i];
          count += item.count;
        }
      }
    }

    return count;
  };

  const getPriceRangeLimit = (num) => {
    return num - (num % 5);
  };

  const defaultRangeOptions = useMemo(() => {
    if (!isRangeFacet(facet)) {
      return [];
    }

    const numericValues = [
      ...new Set(
        facet?.options
          ?.map((item) => Math.round(+item.name.match(/\d+(?:\.\d+)?/)))
          .sort((a, b) => a - b)
      ),
    ];

    const minValue = Math.min(...numericValues);
    const maxValue = Math.max(...numericValues);
    let maxOptions = 5;

    let stepBy = maxValue - minValue;

    if (stepBy > 3) {
      while (true) {
        stepBy = Math.ceil((maxValue - minValue) / maxOptions);

        if (stepBy < 2) {
          maxOptions -= 1;
        } else {
          break;
        }
      }
    }

    const rangeOptions = [];
    let nextMin = minValue;

    for (let i = 0; i < maxOptions; i++) {
      const min = nextMin;
      let max = (i + 1) * stepBy + minValue;

      if (facet.filter.name === FILTER_NAME.PRICE) {
        max = getPriceRangeLimit(max);
      }

      nextMin = max;

      if (max > maxValue) {
        max = maxValue;
      }

      if (min < max) {
        let count = 0;

        if (i === 0) {
          count = countProduct(min - 1, max);
        } else {
          count = countProduct(min, max);
        }

        let label = `${min} To ${max}`;

        if (facet.filter.name === FILTER_NAME.PRICE) {
          label = `$${min} - $${max}`;
        }

        rangeOptions.push({
          min,
          max,
          label,
          count,
        });
      }
    }

    return rangeOptions;
  }, [facet, facets]);

  const isCustomRange = useMemo(() => {
    return facet.filter.customRange;
  }, [facet]);

  const rangeUnit = useMemo(() => {
    if (!isRangeFacet(facet)) {
      return;
    }

    return [
      ...new Set(
        facet?.list?.map((item) =>
          item.name
            .match(/[a-zA-Z]+/)
            ?.toString()
            .toLowerCase()
        )
      ),
    ][0];
  }, [facet]);

  const isDisabled = useMemo(
    () => (filterName, item) => {
      const salsifyName = item.name;
      const salsifyOption = salsifyFacet?.list?.find(
        (option) => option.name === salsifyName
      );
      const count = salsifyOption ? salsifyOption.count : 0;
      return count === 0;
    },
    [url, facets]
  );

  const [optionFilter, setOptionFilter] = useState("");
  const [sortData, setSortData] = useState([]);

  const filterOptions = useCallback((txt) => {
    let options = facet.options;

    if (!options || options.length === 0) {
      setSortData([]);
    }

    const ne = (str) => str.replace(/\d+/g, (n) => n.padStart(8, "0"));
    const nc = (a, b) => ne(a.name).localeCompare(ne(b.name));

    if (txt && txt.length > 0) {
      options = options.filter((item) => {
        const option = item.overwrite ? item.overwrite : item.name;
        return option.toLowerCase().includes(txt.toLowerCase());
      });
    }

    if (options) {
      options = options.sort(nc);
    }

    setSortData(options);

    if (selected) {
      const { type, name, value } = selected;

      const found = options.find((item) => {
        const option = item.overwrite ? item.overwrite : item.name;
        return option.toLowerCase() === value.toLowerCase();
      });

      if (!found) {
        updateUrl(type, name, value);
        setSelected(null);
      }
    }
  });

  const rangeLabel = (option) => {
    let label = option.label;

    if (facet.filter.counts) {
      if (option.count > 0) {
        label = `${label} (${option.count})`;
      } else if (option.originalCount > 0) {
        label = `${label} (${option.originalCount})`;
      }
    }

    return label;
  };

  const optionFilterChanged = useCallback((event) => {
    const txt = event.target.value;
    setOptionFilter(txt);
    filterOptions(txt);
  });

  return (
    <div
      className={classNames([
        `py-4 border-t border-black first:border-0 first:pt-0 lg:py-6`,
        { hidden: !facet.filter.show },
        { close: !facet.filter.expand },
      ])}
      ref={facetBlock}
    >
      <h3
        className={`relative flex justify-between button-text cursor-pointer h4`}
        onClick={toggle}
      >
        <div class="pt-[5px]">{displayName(facet)}</div>
        <span
          ref={minus}
          className={classNames([
            `absolute top-[calc(50%_-_7px)] right-0 inline-block w-[14px] h-[14px] pt-[6px] pb-[5px] pr-[10px] pl-0
          after:content-[''] after:absolute after:w-full after:h-[2px] after:bg-black
          before:content-[''] before:absolute before:w-full before:h-[2px] before:-rotate-180 before:bg-black
          before:transition-[transform] before:ease-[ease-in-out] before:duration-[0.25s] [&.hide]:before:-rotate-90`,
            { hide: !facet.filter.expand },
          ])}
        ></span>
      </h3>
      {isRangeFacet(facet) ? (
        <Fragment>
          <div
            class="[.close>&]:z-[-1] [.close>&]:opacity-0 [.close>&]:invisible transition-height duration-300"
            ref={content}
          >
            <fieldset>
              <legend className="text-[0px] indent-[-999px]">{displayName(facet)}</legend>
              <ul className="mt-[22px] grid gap-3">
                {isRatingFacet(facet) ? (
                  ratingData(facet).map((item) => (
                    <li key={item} className={classNames("text-sm")}>
                      <label className="grid">
                        <input
                          type="checkbox"
                          onChange={onReviewChangeHandler}
                          onKeyPress={onReviewChangeHandler}
                          checked={!!isCheckedReview(facet.filter.name, item)}
                          name={facet.filter.name}
                          value={item}
                          className="mt-[-1px] focus:border-blue facet__input-checkbox sr-only"
                        />
                        <span className="caption">
                        <div
                          className={classNames(
                            "flex",
                            "bv_stars_svg_no_wrap",
                            "text-black-50 [&.active]:text-black",
                            {
                              active: !!isCheckedReview(
                                facet.filter.name,
                                item
                              ),
                            }
                          )}
                          aria-hidden="true"
                          dangerouslySetInnerHTML={{__html: getReview(item)}}
                        />
                      </span>
                      </label>
                    </li>
                  ))
                ) : (
                  <Fragment>
                    {rangeOptions?.map((rangeOption) => (
                      <li key={JSON.stringify(rangeOption)} className="text-sm">
                        <label className="grid grid-flow-col gap-2 grid-cols-[16px_1fr]">
                          <input
                            type="checkbox"
                            onChange={onRangeChangeHandler}
                            onKeyPress={onRangeChangeHandler}
                            checked={
                              !!isCheckedRange(facet.filter.name, rangeOption)
                            }
                            disabled={!!isDisabledRange(rangeOption)}
                            name={facet.filter.name}
                            value={JSON.stringify(rangeOption)}
                            className="mt-[1px] facet__input-checkbox"
                            style={{
                              backgroundColor: isDisabledRange(rangeOption)
                                ? "rgba(0,0,0,0.1)"
                                : "",
                            }}
                          />
                          <span
                            className={classNames("p1", {
                              "font-bold": !!isCheckedRange(
                                facet.filter.name,
                                rangeOption
                              ),
                            })}
                            style={{
                              color: isDisabledRange(rangeOption)
                                ? "rgba(0,0,0,0.5)"
                                : "",
                            }}
                          >
                          {rangeLabel(rangeOption)}
                        </span>
                        </label>
                      </li>
                    ))}

                    {isCustomRange ? (
                      <li
                        key={facet.filter.name + "custom"}
                        className="caption mt-[2px]"
                      >
                        <form
                          className="flex items-center gap-[9px]"
                          onSubmit={onSubmit}
                        >
                          <input
                            type="number"
                            id="Min"
                            name="Min"
                            ref={formMinValue}
                            placeholder="Min"
                            className="w-[52px] h-[41px] py-[12px] px-[1px] border-black p1 text-black placeholder:opacity-60 text-center focus:border-black"
                            min="0"
                            onInput={formMinValueChanged}
                          />
                          ...
                          <input
                            type="number"
                            id="Max"
                            name="Max"
                            ref={formMaxValue}
                            placeholder="Max"
                            className="w-[52px] h-[41px] py-[12px] px-[1px] border-black p1 text-black placeholder:opacity-60 text-center focus:border-black"
                            min="0"
                            onInput={formMaxValueChanged}
                          />
                          <input
                            type="hidden"
                            name="Facet"
                            value={facet.filter.name}
                          />
                          <input
                            type="submit"
                            value={facet.filter.customRangeButton}
                            className="w-[52px] h-[41px] py-[12px] border border-solid border-black p1 bg-white text-black text-center cursor-pointer"
                            disabled={submitDisabled}
                          />
                        </form>
                      </li>
                    ) : (
                      ""
                    )}
                  </Fragment>
                )}
              </ul>
            </fieldset>

            {facet.filter.clear && (
              <div class={classNames(["inline-block"])}>
                <button
                  type="button"
                  class={classNames([
                    "button__clear mt-4 button-text border-b-[1.5px] border-solid border-black",
                    {
                      invisible: !rangeFiltersSet.has(facet.filter.name), // O(1) Checks to show this button
                      "pointer-events-none": !rangeFiltersSet.has(
                        facet.filter.name
                      ),
                    },
                  ])}
                  onClick={(evt) => handleClearRange(evt, facet)}
                >
                  {facet.filter.clear}
                </button>
              </div>
            )}
          </div>
        </Fragment>
      ) : (
        <div
          class="[.close>&]:z-[-1] [.close>&]:opacity-0 [.close>&]:invisible transition-height duration-300"
          style="height: auto; overflow: visible;"
          ref={content}
        >
          <fieldset>
            <legend className="text-[0px] indent-[-999px]">{displayName(facet)}</legend>
            {facet.filter.optionFinder && (
              <div className="mt-[20px] search-container">
                <label for="optionFinder" className="sr-only">{facet.filter.name}</label>
                <input
                  id="optionFinder"
                  name="optionFinder"
                  type="text"
                  className="h-[40px]"
                  value={optionFilter}
                  onInput={optionFilterChanged}
                  placeholder={
                    facet.filter.overwrite
                      ? `Find ${facet.filter.overwrite}`
                      : `Find ${facet.filter.name}`
                  }
                />
              </div>
            )}

            {sortData && sortData.length === 0 && (
              <div class="w-full text-center mt-6">No options found</div>
            )}

            <ul
              class={classNames([
                "mt-[22px] grid gap-2.5",
                {
                  "collection__filter-checkbox-list":
                    facet.options?.length >= OPTIONS_COUNT_IN_GROUP,
                },
              ])}
            >
              {sortData &&
                sortData.map((item) => (
                  <li
                    key={item.name}
                    class={classNames("caption relative", {
                      hidden: !item.show,
                      "opacity-20 point-event-none after:absolute after:left-0 after:top-0 after:w-full after:h-full after:z-10":
                        isDisabled(facet.filter.name, item),
                    })}
                  >
                    <label class="grid grid-flow-col gap-2 grid-cols-[16px_1fr]">
                      {facet.filter.style === "Checkbox" ? (
                        <Fragment>
                          <input
                            type="checkbox"
                            onkeypress={onChangeHandler}
                            onChange={onChangeHandler}
                            checked={!!isChecked(facet.filter.name, item.name)}
                            name={facet.filter.name}
                            value={item.name}
                            class="mt-[1px] facet__input-checkbox"
                            tabindex={
                              isDisabled(facet.filter.name, item) ? "-1" : 0
                            }
                          />
                          <span
                            class={classNames([
                              "p1",
                              {
                                "font-bold": !!isChecked(
                                  facet.filter.name,
                                  item.name
                                ),
                              },
                            ])}
                          >
                          {facet.filter.name === "onSale"
                            ? facetStrings["onSale"]
                            : isCustomFacet(item.name)
                              ? facetStrings[item.name]
                              : itemName(item)}
                        </span>
                        </Fragment>
                      ) : (
                        <Fragment>
                          <input
                            type="radio"
                            onChange={onChangeHandler}
                            onkeypress={onChangeHandler}
                            checked={!!isChecked(facet.filter.name, item.name)}
                            name={facet.filter.name}
                            value={item.name}
                            class="facet__input-checkbox"
                            tabindex={
                              isDisabled(facet.filter.name, item) ? "-1" : 0
                            }
                          />
                          <span
                            class={classNames([
                              "caption input-label",
                              {
                                "font-bold": !!isChecked(
                                  facet.filter.name,
                                  item.name
                                ),
                              },
                            ])}
                          >
                          {facet.filter.name === "onSale"
                            ? facetStrings["onSale"]
                            : isCustomFacet(item.name)
                              ? facetStrings[item.name]
                              : itemName(item)}
                        </span>
                        </Fragment>
                      )}
                    </label>
                  </li>
                ))}
            </ul>
            {facet.filter.clear && (
              <button
                type="button"
                class={classNames([
                  "button__clear mt-4 button-text border-b-[1.5px] border-solid border-black",
                  {
                    invisible: !refinerFiltersSet.has(facet.filter.name), // O(1) Checks to show this button
                    "pointer-events-none": !refinerFiltersSet.has(
                      facet.filter.name
                    ),
                  },
                ])}
                onClick={(evt) => handleClearClick(evt, facet)}
              >
                {facet.filter.clear}
              </button>
            )}
          </fieldset>
        </div>
      )}
    </div>
  );
}
